import {assistantMessageId} from '../runtime/turn';
import type {Thread, ToolCallPart} from '../store/thread';
import {askUserContinuation, type AskUserResult} from './askUser';
import type {ContinuationSpec, PendingPayload} from './continuationSpec';
import {
  networkAccessContinuation,
  type NetworkAccessResult,
} from './networkAccess';

/** WBAgentContinuation is the union of the registered continuation specs. */
export type WBAgentContinuation =
  | ContinuationSpec<AskUserResult>
  | ContinuationSpec<NetworkAccessResult>;

/**
 * PendingToolInteraction identifies the single actionable HITL call on a
 * thread, anchored to its exact tool-call part.
 */
export type PendingToolInteraction = {
  messageId: string;
  toolCallId: string;
  toolName: string;
  payload: PendingPayload;
};

/**
 * CONTINUATIONS_BY_TOOL_NAME are all HITL continuations, keyed by backend tool
 * name.
 */
export const CONTINUATIONS_BY_TOOL_NAME: Readonly<
  Record<string, WBAgentContinuation>
> = {
  [askUserContinuation.toolName]: askUserContinuation,
  [networkAccessContinuation.toolName]: networkAccessContinuation,
};

/**
 * resolvePendingToolInteraction returns the single actionable HITL call on the
 * thread, anchored to its exact tool-call part, or null when none is actionable.
 *
 * NOTE: Actionable requires the matching call at the active-branch leaf, so a
 * child turn or branch switch reverts it to historical. Network wins over
 * questions.
 *
 * TODO: Drop the network-over-questions precedence and resolve each pending
 * call independently once the backend can resolve a tool call without starting
 * a new turn. Today only one call is actionable because resolving it appends a
 * child turn that reverts the rest to historical.
 */
export function resolvePendingToolInteraction(
  thread: Thread | null | undefined
): PendingToolInteraction | null {
  if (!thread || thread.latestTurnId == null) {
    return null;
  }

  const payload = readPendingPayload(thread);
  if (!payload) {
    return null;
  }

  const toolName =
    payload.kind === 'network-access'
      ? networkAccessContinuation.toolName
      : askUserContinuation.toolName;

  const {messageTree} = thread;
  const messageId = assistantMessageId(thread.latestTurnId);
  // Actionable only at the active-branch leaf.
  if (messageTree.currentNodeId !== messageId) {
    return null;
  }

  const toolCall = messageTree.nodes[messageId]?.content.find(
    (part): part is ToolCallPart =>
      part.type === 'tool-call' && part.toolName === toolName
  );
  if (!toolCall) {
    return null;
  }

  return {messageId, toolCallId: toolCall.toolCallId, toolName, payload};
}

/**
 * resolveHITLSendBlock reports whether every send path (composer and
 * programmatic) must block, derived from the part-anchored
 * `resolvePendingToolInteraction` so the block matches the inline controls
 * exactly. Used by both the composer and the `onNew`/`onEdit`/`onReload`
 * runtime guard.
 */
export function resolveHITLSendBlock(
  thread: Thread | null | undefined
): boolean {
  const interaction = resolvePendingToolInteraction(thread);
  return (
    interaction != null &&
    CONTINUATIONS_BY_TOOL_NAME[interaction.toolName].composerPolicy === 'block'
  );
}

function readPendingPayload(thread: Thread): PendingPayload | null {
  if (thread.pendingNetworkAccessRequest != null) {
    return {
      kind: 'network-access',
      request: thread.pendingNetworkAccessRequest,
    };
  }
  if (thread.pendingQuestions != null && thread.pendingQuestions.length > 0) {
    return {kind: 'questions', questions: thread.pendingQuestions};
  }

  return null;
}
